// How a party (a name on one recorded transaction) is described on screen.
// A party is never a person: the same name on two transactions may be two different people.

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

// For visitors without an active subscription the API withholds the price and address (party.masked) and says only
// whether they exist (party.sale, party.hasAddress); these read either shape.
export function isSale(party) {
  return party.sale === true || party.transferAmount > 0;
}

export function hasAddress(party) {
  return party.hasAddress === true || Boolean(party.propertyAddressFull);
}

// Grantee = received the property, grantor = transferred it. Only a transfer with a price is called a sale,
// since many deeds (quitclaims, transfers into a trust) aren't.
export function describeRole(party) {
  const sale = isSale(party);
  if (party.role === 'grantee') {
    return sale ? 'Bought' : 'Received';
  }
  return sale ? 'Sold' : 'Transferred';
}

export function describeAddress(party) {
  return [party.propertyAddressFull, party.propertyAddressCity].filter(Boolean).join(', ');
}

export function describeDate(party) {
  if (!party.recordingDate) {
    return null;
  }
  // yyyy-MM-dd from the API; format in UTC so the date doesn't shift a day in US time zones
  return new Date(`${party.recordingDate}T00:00:00Z`).toLocaleDateString('en-US', {timeZone: 'UTC'});
}

// Shown instead of the price and address to visitors without an active subscription. Fixed length, so the mask
// doesn't hint at the real value.
export const MASKED_PRICE = '$•••,•••';
export const MASKED_ADDRESS = '•••• ••••••••, ••••••';

export function describeTransaction(party, masked = false) {
  const hidden = masked || party.masked || party.transferAmount == null;
  const price = isSale(party) ? (hidden ? MASKED_PRICE : currencyFormatter.format(party.transferAmount)) : null;
  return [describeRole(party), describeDate(party), price].filter(Boolean).join(' · ');
}

// The line under the name: what happened, when, for how much, and where; missing parts are left out.
// With masked, the role and date stay readable and the price and address are replaced by MASKED_PRICE/ADDRESS.
// A result is shown masked when the page asks for it or the API already masked it.
export function describeParty(party, masked = false) {
  const hidden = masked || party.masked || !describeAddress(party);
  const address = hasAddress(party) ? (hidden ? MASKED_ADDRESS : describeAddress(party)) : null;
  return [describeTransaction(party, masked), address].filter(Boolean).join(' · ');
}

export function partyKey(party) {
  return `${party.transactionId}-${party.propertyId}-${party.role}-${party.nameFull}`;
}

// All grantor or grantee names on a transaction from /transactions/findById (slots 1-4), blanks left out.
// Party search indexes every slot, so a name it finds must show up here too.
export function partyNames(transaction, role) {
  return [1, 2, 3, 4]
    .map(slot => transaction[`${role}${slot}NameFull`])
    .filter(name => name && name.trim())
    .map(name => name.trim())
    .join(', ');
}
